/**
 * AudioOwnership — exactly one Moonfall page makes sound at a time.
 *
 * Mobile browsers keep background tabs playing audio. An earlier Moonfall page
 * therefore carries on with its own `<audio>` element while the page in front
 * of the player owns a different one, and the visible page's volume control
 * cannot reach the element that is actually making noise. Reported as: "it's
 * like some old still playback ... that's why I think the actual volume button
 * doesn't work, because it's not even the most recent playback."
 *
 * The newest claim wins. Every other page releases its media immediately, so
 * whatever the player can hear is always the page whose controls they can see.
 */
/**
 * Declared rather than inferred. The real object is built inside the IIFE
 * below, so TypeScript would otherwise widen this to `{}` and every caller —
 * soundtrack.js has six — would report the whole API as missing.
 */
type AudioOwnershipApi = {
    register(release: () => void): void;
    claim(): void;
    holdsAudio(): boolean;
};

let AudioOwnership = {} as AudioOwnershipApi;

(function () {
    'use strict';


    var CHANNEL_NAME = 'moonfall-audio-ownership';

    /* Distinguishes THIS page from every other one. A page must never evict
       itself: without an identity, its own broadcast comes back through the
       channel in some browsers and silences the page that just claimed. */
    var pageId = 'p-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);

    var channel = null;
    var releaseHandlers = [];
    /* Optimistic: a page with no way to coordinate is the only page there is,
       and telling it that it does not own audio would silence the whole app. */
    var owns = true;

    function openChannel() {
        if (channel || typeof window.BroadcastChannel !== 'function') return channel;
        try {
            channel = new window.BroadcastChannel(CHANNEL_NAME);
            channel.onmessage = function (event) {
                var data = event && event.data;
                if (!data || data.type !== 'claim') return;
                if (data.pageId === pageId) return; // our own claim, echoed back
                if (!owns) return;
                owns = false;
                releaseAll();
            };
        } catch (_) {
            channel = null; // no coordination available; stay optimistic
        }
        return channel;
    }

    function releaseAll() {
        for (var i = 0; i < releaseHandlers.length; i += 1) {
            try {
                releaseHandlers[i]();
            } catch (error) {
                /* One page's broken teardown must not stop the others from
                   releasing, and must never propagate into the claiming page. */
                console.warn('[AudioOwnership] release handler failed:', error);
            }
        }
    }

    /**
     * Register something to silence when another page takes the audio.
     * @param {Function} release called with no arguments; must be idempotent.
     */
    function register(release) {
        if (typeof release === 'function') releaseHandlers.push(release);
    }

    /**
     * Claim the audio for this page. Safe to call repeatedly — playback start,
     * a real user interaction, or the page becoming visible again.
     */
    function claim() {
        owns = true;
        var bus = openChannel();
        if (!bus) return;
        try {
            bus.postMessage({ type: 'claim', pageId: pageId });
        } catch (_) { /* a closed channel just means we are alone */ }
    }

    /** @returns {boolean} whether this page is the one allowed to make sound. */
    function holdsAudio() {
        return owns;
    }

    AudioOwnership = {
        register: register,
        claim: claim,
        holdsAudio: holdsAudio,
    };
}());

export { AudioOwnership };
